import { FormEvent, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import {
	type Auth,
	createUserWithEmailAndPassword,
	getAuth,
	sendPasswordResetEmail,
	signInWithEmailAndPassword,
} from 'firebase/auth'

import { track } from '../analytics'
import { GoogleButton } from '../components/google-button'
import { NaydaStacked } from '../components/nayda-mark'
import { firebaseApp } from '../firebase'

export function Login() {
	const navigate = useNavigate()
	const [email, setEmail] = useState('')
	const [password, setPassword] = useState('')
	const [showPassword, setShowPassword] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const [notice, setNotice] = useState<string | null>(null)
	const [loading, setLoading] = useState(false)
	const [creating, setCreating] = useState(false)

	async function handleSubmit(e: FormEvent) {
		e.preventDefault()
		setError(null)
		setNotice(null)
		setLoading(true)
		const auth = getAuth(firebaseApp)
		try {
			await signInWithEmailAndPassword(auth, email, password)
			navigate('/')
		} catch (err) {
			if (!isWrongEmailOrPassword(err)) {
				setError(humanizeFirebaseError(err))
				return
			}
			// People who have never signed up keep pressing "Sign in" first. Treat
			// that as a sign-up rather than sending them away to another form.
			setCreating(true)
			setError(await signUpInstead(auth, email, password, navigate))
		} finally {
			setLoading(false)
			setCreating(false)
		}
	}

	async function handleForgotPassword() {
		setError(null)
		setNotice(null)
		if (!email.trim()) {
			setError('Type your email above first, then click "Forgot password?" again.')
			return
		}
		try {
			const auth = getAuth(firebaseApp)
			await sendPasswordResetEmail(auth, email.trim())
			setNotice(`If ${email.trim()} has an account, a reset link is on its way. Check your inbox.`)
		} catch (err) {
			// Don't leak whether the email exists — show the same notice as success.
			const code = (err as { code?: string })?.code
			if (code === 'auth/invalid-email') {
				setError('That email address looks malformed.')
				return
			}
			setNotice(`If ${email.trim()} has an account, a reset link is on its way. Check your inbox.`)
		}
	}

	return (
		<div className="min-h-screen bg-bone flex items-center justify-center p-6">
			<div className="w-full max-w-[400px]">
				{/* Stacked Nayda lockup */}
				<div className="mb-10 flex justify-center">
					<NaydaStacked markSize={80} />
				</div>

				{/* Card */}
				<div className="card p-8">
					<div className="mb-6">
						<h1 className="font-sans font-medium text-[26px] leading-tight text-ink">Welcome back.</h1>
						<p className="text-ink-soft text-[14px] mt-1">A loyal dog for every locale.</p>
					</div>

					<GoogleButton onError={setError} />

					<div className="flex items-center gap-3 my-5">
						<div className="h-px flex-1 bg-line" />
						<span className="label-mono">or</span>
						<div className="h-px flex-1 bg-line" />
					</div>

					<form
						onSubmit={(e) => void handleSubmit(e)}
						className="flex flex-col gap-4"
					>
						<label className="flex flex-col gap-1.5">
							<span className="field-label">Email</span>
							<input
								type="email"
								required
								autoFocus
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								className="field"
								placeholder="you@company.com"
							/>
						</label>

						<label className="flex flex-col gap-1.5">
							<span className="field-label">Password</span>
							<div className="relative">
								<input
									type={showPassword ? 'text' : 'password'}
									required
									value={password}
									onChange={(e) => setPassword(e.target.value)}
									className="field pr-11"
									placeholder="••••••••"
								/>
								<button
									type="button"
									onClick={() => setShowPassword((v) => !v)}
									aria-label={showPassword ? 'Hide password' : 'Show password'}
									aria-pressed={showPassword}
									className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-nayda-sm text-ink-soft hover:text-ink hover:bg-bone focus:outline-none focus-visible:ring-2 focus-visible:ring-marigold"
								>
									<EyeIcon shown={showPassword} />
								</button>
							</div>
							<button
								type="button"
								onClick={() => void handleForgotPassword()}
								className="self-end mt-1 text-[12px] text-ink-soft hover:text-ink underline underline-offset-2"
							>
								Forgot password?
							</button>
						</label>

						{error && (
							<div className="text-[13px] text-[#B23B2A] bg-[#B23B2A]/10 rounded-nayda-sm px-3 py-2 border border-[#B23B2A]/20">
								{error}
							</div>
						)}
						{notice && !error && (
							<div className="text-[13px] text-ink bg-marigold/15 rounded-nayda-sm px-3 py-2 border border-marigold/40">
								{notice}
							</div>
						)}

						<button
							type="submit"
							disabled={loading}
							className="btn mt-2"
						>
							{creating ? 'Creating your account…' : loading ? 'Signing in…' : 'Sign in'}
						</button>

						<p className="text-[12px] text-ink-mute text-center mt-1">
							New here? Enter your email and a password — we'll create your account.
						</p>
					</form>
				</div>

				{/* Footer */}
				<div className="mt-6 text-center text-[13px] text-ink-soft">
					Don't have an account?{' '}
					<Link
						to="/signup"
						className="text-ink underline underline-offset-2 hover:text-ember"
					>
						Create one
					</Link>
				</div>
			</div>
		</div>
	)
}

/**
 * Eye icon that toggles between "open" (password visible) and "closed with
 * slash" (password hidden). 18px stroke icon matching field height.
 */
function EyeIcon({ shown }: { shown: boolean }) {
	if (shown) {
		// Eye open
		return (
			<svg
				width="18"
				height="18"
				viewBox="0 0 24 24"
				fill="none"
				stroke="currentColor"
				strokeWidth="2"
				strokeLinecap="round"
				strokeLinejoin="round"
				aria-hidden
			>
				<path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" />
				<circle
					cx="12"
					cy="12"
					r="3"
				/>
			</svg>
		)
	}
	// Eye with slash
	return (
		<svg
			width="18"
			height="18"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden
		>
			<path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-10-8-10-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 10 8 10 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
			<line
				x1="1"
				y1="1"
				x2="23"
				y2="23"
			/>
		</svg>
	)
}

/**
 * Firebase answers a missing account and a wrong password with the same code
 * (email enumeration protection), so the sign-in error alone can't tell a
 * newcomer from a typo. `auth/user-not-found` still turns up when protection
 * is off.
 */
function isWrongEmailOrPassword(err: unknown): boolean {
	const code = (err as { code?: string })?.code
	return (
		code === 'auth/invalid-login-credentials' ||
		code === 'auth/invalid-credential' ||
		code === 'auth/user-not-found' ||
		code === 'auth/wrong-password'
	)
}

/**
 * Sign-up is the one call that does reveal whether an email is taken, so it
 * doubles as the check: it either creates the account (the person was new) or
 * fails with `email-already-in-use` (the account exists, the password was
 * wrong). Returns the message to show, or null after navigating away.
 */
async function signUpInstead(
	auth: Auth,
	email: string,
	password: string,
	navigate: (to: string) => void
): Promise<string | null> {
	// Firebase rejects passwords under 6 characters on sign-up, and the reply
	// would say nothing about whether the account exists.
	if (password.length < 6) {
		return "Wrong email or password. New here? Pick a password of at least 6 characters and we'll create your account."
	}
	try {
		await createUserWithEmailAndPassword(auth, email, password)
		// The Nayda account itself is created on the first API call (AccountService.Me).
		track('signup_from_login')
		navigate('/')
		return null
	} catch (err) {
		const code = (err as { code?: string })?.code
		if (code === 'auth/email-already-in-use') {
			return 'Wrong password for this email. If you signed up with Google, use the button above; otherwise use "Forgot password?".'
		}
		return humanizeFirebaseError(err)
	}
}

function humanizeFirebaseError(err: unknown): string {
	const code = (err as { code?: string })?.code
	// Newer Firebase SDK returns the anti-enumeration code for both wrong email
	// and wrong password — by design, so attackers can't tell which is wrong.
	if (code === 'auth/invalid-login-credentials' || code === 'auth/invalid-credential') {
		return "Wrong email or password. If you've forgotten it, use the reset link below."
	}
	if (code === 'auth/user-not-found' || code === 'auth/wrong-password') {
		return "Wrong email or password. If you've forgotten it, use the reset link below."
	}
	if (code === 'auth/invalid-email') {
		return 'That email address looks malformed.'
	}
	if (code === 'auth/user-disabled') {
		return 'This account is disabled. Contact support.'
	}
	if (code === 'auth/too-many-requests') {
		return 'Too many attempts. Try again in a minute, or reset your password.'
	}
	if (code === 'auth/network-request-failed') {
		return 'Network problem — check your connection and try again.'
	}
	return err instanceof Error ? err.message : 'Login failed'
}
